import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { conf } from '../../domain/services/configService';
import { metaDataServiceHelper } from '../../domain/services/metaDataService';
import ShareLink from '../ShareLink';
import { getMobileOperatingSystem } from '../../domain/utils/userAgentUtils';
import { ShareKitMobile } from './components/ShareKitMobile';
import { ShareKitDesktop } from './components/ShareKitDesktop';
import { shareService } from '../../domain/services/shareService';

const ngSharekitDefaultLinks = css`
  display: flex;

  & a {
    font-size: 1.4375rem;
    margin-right: 25px;
  }

  & a:nth-of-type(1) {
    margin-right: 20px;
  }
`;

export const ShareKit = ({ type, content, gtmData, color, useBrowserUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileOs, setMobileOs] = useState();

  useEffect(() => {
    setMobileOs(getMobileOperatingSystem());
  }, []);

  const openStateHandler = () => {
    setIsOpen(!isOpen);
  };

  if (type === 'article') {
    const domain = conf.isStage
      ? `http://fng-globalsites-stage-html-current-${conf.siteCountryCode}.s3-website.eu-central-1.amazonaws.com`
      : `https://${conf.canonicalDomain}`;
    const fullUrl = `${domain}${content.url?.path}`;
    const title = metaDataServiceHelper.getMetaTitle(content, 'og:title', true);
    if (mobileOs === 'unknown') {
      const position = content.bundle === 'photo_gallery' ? '25%' : '50%';
      return (
        <ShareKitDesktop
          color={color}
          gtmData={gtmData}
          url={fullUrl}
          openStateHandler={openStateHandler}
          isOpen={isOpen}
          title={title}
          position={position}
          useBrowserUrl={useBrowserUrl}
        />
      );
    }

    if (mobileOs) {
      const shareHandler = () => {
        if (typeof navigator === 'object' && navigator.share) {
          navigator.share({
            title,
            text: title,
            url: shareService.getBrowserUrl()
          });
        } else {
          setMobileOs('unknown');
          setIsOpen(true);
        }
      };

      return <ShareKitMobile handler={shareHandler} mobileOs={mobileOs} />;
    }
    return null;
  }
  // Build the default links.
  return (
    <div>
      <div css={ngSharekitDefaultLinks}>
        {conf.menus.menuSocialMedia.facebookUrl && (
          <ShareLink
            type="facebook"
            link
            url={conf.menus.menuSocialMedia.facebookUrl}
            gtmData={gtmData}
            color={color}
            popUp={false}
            useBrowserUrl={useBrowserUrl}
          />
        )}
        {conf.menus.menuSocialMedia.twitterUrl && (
          <ShareLink
            type="twitter"
            link
            url={conf.menus.menuSocialMedia.twitterUrl}
            via={conf.menus.menuSocialMedia.twitterHandle}
            gtmData={gtmData}
            color={color}
            popUp={false}
            useBrowserUrl={useBrowserUrl}
          />
        )}
        {conf.menus.menuSocialMedia.instagramUrl && (
          <ShareLink
            type="instagram"
            link
            url={conf.menus.menuSocialMedia.instagramUrl}
            gtmData={gtmData}
            color={color}
            popUp={false}
            useBrowserUrl={useBrowserUrl}
          />
        )}
        {conf.menus.menuSocialMedia.flipboardUrl && (
          <ShareLink
            type="flipboard"
            link
            url={conf.menus.menuSocialMedia.flipboardUrl}
            gtmData={gtmData}
            color={color}
            popUp={false}
            useBrowserUrl={useBrowserUrl}
          />
        )}
        {conf.menus.menuSocialMedia.youtubeChannel && (
          <ShareLink
            type="youtube"
            link
            url={`https://www.youtube.com/${conf.menus.menuSocialMedia.youtubeChannel}`}
            gtmData={gtmData}
            color={color}
            popUp={false}
            useBrowserUrl={useBrowserUrl}
          />
        )}
        <ShareLink
          type="rss"
          link
          url={`https://${conf.canonicalDomain}/api/rss/latest_contents.xml`}
          gtmData={gtmData}
          color={color}
          popUp={false}
          useBrowserUrl={useBrowserUrl}
        />
      </div>
    </div>
  );
};

ShareKit.defaultProps = {
  type: undefined,
  url: undefined,
  content: undefined,
  gtmData: [],
  color: undefined,
  useBrowserUrl: false
};
ShareKit.propTypes = {
  type: PropTypes.oneOf(['article', 'site-nav', 'newsletter']),
  url: PropTypes.string,
  content: PropTypes.objectOf(PropTypes.any),
  gtmData: PropTypes.arrayOf(
    PropTypes.shape({
      var: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  color: PropTypes.string,
  useBrowserUrl: PropTypes.bool
};
